import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputNumberModule } from 'primeng/inputnumber';
import { ToastModule } from 'primeng/toast';
import { EvaluationCategoryGroupSalaryRange } from '../../../../../core/models/evaluation-category-group-salary-range';
import { EvaluationCategoryGroupSalaryRangeService } from '../../../../../core/services/evaluation-category-group-salary-range.service';
import { PracticeScopeService } from '../../../../../core/services/practice-scope.service';
import { RequiredMarkDirective } from '../../../../../shared/directives/required-mark.directive';

interface CategoryOption {
  label: string;
  value: string;
}

/**
 * Adds the four salary reference values of a category that has no range for the
 * chosen practice yet. The practice is preselected from the grid, but can be
 * changed; the category list follows the practice and only offers the labels that
 * still miss a range, so the same range cannot be created twice.
 */
@Component({
  selector: 'em-evaluation-salary-range-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DropdownModule,
    InputNumberModule,
    ToastModule,
    TranslatePipe,
    RequiredMarkDirective,
  ],
  templateUrl: './evaluation-salary-range-form.component.html',
  styleUrl: './evaluation-salary-range-form.component.scss',
  providers: [MessageService],
})
export class EvaluationSalaryRangeFormComponent implements OnInit {
  form!: FormGroup;
  practices: string[] = [];
  availableCategoriesByPractice: { [practice: string]: string[] } = {};
  categoryOptions: CategoryOption[] = [];
  translations: any = {};
  errorMessages: any = {};
  /** True for a Manager / Manager People: their practice is already fixed. */
  readonly isPracticeScoped: boolean;

  constructor(
    private fb: FormBuilder,
    private evaluationCategoryGroupSalaryRangeService: EvaluationCategoryGroupSalaryRangeService,
    private ref: DynamicDialogRef,
    private messageService: MessageService,
    private config: DynamicDialogConfig,
    private translate: TranslateService,
    private practiceScope: PracticeScopeService,
  ) {
    this.isPracticeScoped = this.practiceScope.isPracticeScopedUser();
  }

  ngOnInit(): void {
    this.loadTranslations();

    const data = this.config.data;
    this.practices = data?.practices ?? [];
    this.availableCategoriesByPractice = data?.availableCategoriesByPractice ?? {};
    this.categoryOptions = this.toOptions((data?.availableCategoriesByPractice?.[data?.practice] as string[] | undefined) ?? []);

    this.form = this.fb.group({
      practice: [data?.practice ?? null, data?.practices?.length ? [Validators.required] : []],
      categoryShortLabel: [null, [Validators.required]],
      lowSalary: [null, [Validators.min(0)]],
      mediumSalary: [null, [Validators.min(0)]],
      highSalary: [null, [Validators.min(0)]],
      exceedSalary: [null, [Validators.min(0)]],
    });

    this.translate.onLangChange.subscribe(() => this.loadTranslations());
  }

  /**
   * Selects the whole amount as soon as a field takes focus, so typing overwrites
   * the value instead of being appended to it: an update needs no clearing first.
   */
  selectAllOnFocus(event: Event): void {
    (event.target as HTMLInputElement | null)?.select();
  }

  onPracticeChange(): void {
    const practice = this.form.get('practice')?.value;
    this.categoryOptions = this.toOptions(
      (this.availableCategoriesByPractice?.[practice] as string[] | undefined) ?? [],
    );
    this.form.get('categoryShortLabel')?.setValue(null);
  }

  private toOptions(labels: string[]): CategoryOption[] {
    return labels.map((label) => ({ label, value: label }));
  }

  private loadTranslations(): void {
    this.translate
      .get([
        'layout.configurations.evaluation.salaryRanges.rangeForm.practice',
        'layout.configurations.evaluation.salaryRanges.rangeForm.practiceRequired',
        'layout.configurations.evaluation.salaryRanges.rangeForm.category',
        'layout.configurations.evaluation.salaryRanges.rangeForm.categoryPlaceholder',
        'layout.configurations.evaluation.salaryRanges.rangeForm.categoryRequired',
        'layout.configurations.evaluation.salaryRanges.rangeForm.low',
        'layout.configurations.evaluation.salaryRanges.rangeForm.medium',
        'layout.configurations.evaluation.salaryRanges.rangeForm.high',
        'layout.configurations.evaluation.salaryRanges.rangeForm.exceed',
        'layout.configurations.evaluation.salaryRanges.rangeForm.negative',
        'layout.configurations.evaluation.salaryRanges.rangeForm.save',
        'layout.configurations.evaluation.salaryRanges.rangeForm.noCategories',
        'layout.configurations.evaluation.salaryRanges.addRangeDialog.errorMessage.title',
        'layout.configurations.evaluation.salaryRanges.addRangeDialog.errorMessage.detail',
      ])
      .subscribe((translations) => {
        this.translations = translations;
        this.errorMessages = {
          practiceRequired: translations['layout.configurations.evaluation.salaryRanges.rangeForm.practiceRequired'],
          categoryShortLabelRequired:
            translations['layout.configurations.evaluation.salaryRanges.rangeForm.categoryRequired'],
          negative: translations['layout.configurations.evaluation.salaryRanges.rangeForm.negative'],
        };
      });
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.value;

    const range: EvaluationCategoryGroupSalaryRange = {
      id: null,
      categoryShortLabel: value.categoryShortLabel,
      practice: value.practice ?? null,
      lowSalary: value.lowSalary ?? null,
      mediumSalary: value.mediumSalary ?? null,
      highSalary: value.highSalary ?? null,
      exceedSalary: value.exceedSalary ?? null,
    };

    this.evaluationCategoryGroupSalaryRangeService.save(range).subscribe({
      next: (saved) => this.ref.close(saved),
      error: (error) => {
        console.log('Error while saving the salary range: ', error);
        this.messageService.add({
          severity: 'error',
          summary: this.translations['layout.configurations.evaluation.salaryRanges.addRangeDialog.errorMessage.title'],
          detail:
            error?.error?.message ??
            this.translations['layout.configurations.evaluation.salaryRanges.addRangeDialog.errorMessage.detail'],
        });
      },
    });
  }

  isInvalid(key: string): { invalid: boolean; message: string } {
    const control = this.form.get(key);

    return {
      invalid: !!control?.invalid && (control.dirty || control.touched),
      message: control?.hasError('required')
        ? this.errorMessages[`${key}Required`]
        : control?.hasError('min')
          ? this.errorMessages.negative
          : '',
    };
  }
}
